import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clock3,
  MinusCircle,
  SearchX,
  XCircle,
} from 'lucide-react';

import type { CorpusRunStatus, CorpusValueStatus } from '../../types';

const valueConfig: Record<CorpusValueStatus, {
  label: string;
  className: string;
  icon: React.ComponentType<{ className?: string }>;
}> = {
  filled: { label: 'מאומת', className: 'bg-success-50 text-success-800 border-success-200', icon: CheckCircle2 },
  calculated: { label: 'מחושב', className: 'bg-primary-50 text-primary-800 border-primary-200', icon: CheckCircle2 },
  needs_review: { label: 'לבדיקה', className: 'bg-warning-50 text-warning-800 border-warning-200', icon: Clock3 },
  missing: { label: 'לא נמצא', className: 'bg-gray-100 text-gray-700 border-gray-200', icon: SearchX },
  not_applicable: { label: 'לא רלוונטי', className: 'bg-gray-50 text-gray-500 border-gray-200', icon: MinusCircle },
  flagged: { label: 'לא אומת', className: 'bg-warning-50 text-warning-800 border-warning-200', icon: AlertTriangle },
};

export const ValueStatusBadge: React.FC<{ status: CorpusValueStatus }> = ({ status }) => {
  const config = valueConfig[status];
  const Icon = config.icon;
  return (
    <span className={`inline-flex min-h-7 items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${config.className}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {config.label}
    </span>
  );
};

const runConfig: Record<CorpusRunStatus, {
  label: string;
  className: string;
  icon: React.ComponentType<{ className?: string }>;
}> = {
  pending: { label: 'ממתין', className: 'bg-gray-100 text-gray-700', icon: Clock3 },
  running: { label: 'מחלץ נתונים', className: 'bg-primary-50 text-primary-800', icon: CircleDashed },
  done: { label: 'הושלם', className: 'bg-success-50 text-success-800', icon: CheckCircle2 },
  error: { label: 'נכשל', className: 'bg-error-50 text-error-800', icon: XCircle },
};

export const RunStatusBadge: React.FC<{ status: CorpusRunStatus }> = ({ status }) => {
  const config = runConfig[status];
  const Icon = config.icon;
  const animated = status === 'pending' || status === 'running';
  return (
    <span className={`inline-flex min-h-7 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${config.className}`}>
      <Icon className={`h-3.5 w-3.5 ${animated ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden="true" />
      {config.label}
    </span>
  );
};

export const DocumentStatusBadge: React.FC<{ status: string; progress?: number | null }> = ({ status, progress }) => {
  if (status === 'processed') {
    return (
      <span className="inline-flex min-h-7 items-center gap-1 rounded-full bg-success-50 px-2.5 py-1 text-xs font-medium text-success-800">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> מוכן
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex min-h-7 items-center gap-1 rounded-full bg-error-50 px-2.5 py-1 text-xs font-medium text-error-800">
        <XCircle className="h-3.5 w-3.5" aria-hidden="true" /> העיבוד נכשל
      </span>
    );
  }
  return (
    <span className="inline-flex min-h-7 items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-800">
      <CircleDashed className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      בעיבוד{typeof progress === 'number' ? ` · ${progress}%` : ''}
    </span>
  );
};
